// Navigation.js - Category Dropdown Navigation Component (MARKET-AWARE VERSION)
import React, { useState, useRef, useEffect } from 'react';
import { 
  Home,
  BarChart3, 
  Building2, 
  Target, 
  PieChart, 
  Briefcase,
  ChevronDown,
  TrendingUp,
  AlertTriangle,
  GitCompare,
  Activity,
  Zap,
  Search,
  BarChart2,
  Wallet,
  Star,
  Settings,
  GraduationCap,
  Coins,
  Grid3X3,
  Calculator,
  Building,
  UserCheck,
  Scale,       
  Globe,     
  AlertCircle,
  BookOpen,
  Rocket,
  Trophy,
  Calendar,
  Lock
} from 'lucide-react';
import { useMarket } from '../context/MarketContext';

const Navigation = ({ activeTab, setActiveTab, isAuthenticated }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, alignRight: false });
  const navRef = useRef(null);
  
  // Get market context
  const { selectedMarket, isFeatureAvailable } = useMarket();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on scroll
  useEffect(() => {
    const handleScroll = () => {
      setOpenDropdown(null);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle internal navigation events from SportsPrivateMarket buttons
  useEffect(() => {
    const handler = (e) => {
      const dest = e.detail;
      const tabId = dest === 'org-application' ? 'sports-org-portal' : dest;
      setActiveTab(tabId);
      setOpenDropdown(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('navigateTo', handler);
    window.addEventListener('investorq:navigate', handler);
    return () => {
      window.removeEventListener('navigateTo', handler);
      window.removeEventListener('investorq:navigate', handler);
    };
  }, [setActiveTab]);

  // Features that are only available for US market
  const US_ONLY_FEATURES = [
    'congress-trades',
    'whale-watcher', 
    'crypto',
    'crypto-correlation',
    'gamma-exposure',
    'volatility-surface',
    'gex-history',
    'gex-alerts'
  ];

  // Navigation categories with their items
  const allCategories = [
    {
      id: 'markets',
      label: 'Markets',
      icon: BarChart3,
      color: '#3b82f6',
      items: [
        { id: 'market', label: 'Market Overview', icon: TrendingUp, description: selectedMarket === 'IN' ? 'NSE/BSE indices & data' : 'Live indices & Fed data' },
        { id: 'recession', label: 'Recession Dashboard', icon: AlertTriangle, description: selectedMarket === 'IN' ? 'India economic health' : 'US recession indicators' },
        { id: 'ipo-calendar', label: '🚀 IPO Calendar', icon: Rocket, description: 'Upcoming & recent IPOs' },
        { id: 'forex', label: '💱 Forex', icon: Globe, description: 'Currency exchange rates' }
      ]
    },
    {
      id: 'stocks',
      label: 'Stocks',
      icon: Building2,
      color: '#22c55e',
      items: [
        { id: 'company', label: 'Company Analysis', icon: Building2, description: 'Deep dive into any stock' },
        { id: 'technical-analysis', label: '📊 Technical Analysis', icon: Activity, description: '50+ indicators with AI insights' },
        { id: 'compare', label: 'Stock Comparison', icon: GitCompare, description: 'Compare multiple stocks' },
        { id: 'anomaly-detector', label: '⚠️ Anomaly Detector', icon: AlertTriangle },
        { id: 'top-winners', label: '🏆 Top Winners', icon: Trophy, description: 'AI multi-signal stock picks' },
      ]
    },
    {
      id: 'options',
      label: 'Options',
      icon: Target,
      color: '#a855f7',
      items: [
        { id: 'options', label: 'Options Chain', icon: Activity, description: selectedMarket === 'IN' ? 'NSE Options data' : 'Full options data' },
        { id: 'iv-crush', label: 'IV Crush Predictor', icon: Zap, description: 'Earnings volatility analysis' },
        { id: 'gamma-exposure', label: '🔥 Gamma Exposure (GEX)', icon: Activity, description: 'Market maker positioning', usOnly: true },
        { id: 'volatility-surface', label: '📊 3D Vol Surface', icon: BarChart2, description: 'Interactive IV visualization', usOnly: true },
        { id: 'opex-calendar', label: '📅 OpEx Calendar', icon: Activity, description: 'Expiration dates & GEX predictions' },
        { id: 'gex-history', label: '📈 Historical GEX', icon: TrendingUp, description: 'GEX changes over time', usOnly: true },
        { id: 'gex-alerts', label: '🔔 GEX Alerts', icon: AlertTriangle, description: 'Real-time level monitoring', usOnly: true }
      ]
    },
    {
      id: 'etfs',
      label: 'ETFs',
      icon: PieChart,
      color: '#f59e0b',
      items: [
        { id: 'etf-xray', label: 'ETF X-Ray', icon: Search, description: 'Holdings breakdown' },
        { id: 'etf-compare', label: 'ETF Compare', icon: BarChart2, description: 'Side-by-side ETF analysis' }
      ]
    },
    {
      id: 'crypto-cat',
      label: 'Crypto',
      icon: Coins,
      color: '#f59e0b',
      usOnly: true, // Entire category is US only
      items: [
        { id: 'crypto', label: 'Crypto Deep Dive', icon: Activity, description: 'Full cryptocurrency analysis', usOnly: true },
        { id: 'crypto-correlation', label: 'Correlation Matrix', icon: Grid3X3, description: 'How cryptos move together', usOnly: true }
      ]
    },
    {
      id: 'portfolio',
      label: 'Portfolio',
      icon: Briefcase,
      color: '#ec4899',
      items: [
        { id: 'portfolio', label: 'My Portfolio', icon: Wallet, description: 'Track your investments' },
        { id: 'watchlist', label: 'Watchlist', icon: Star, description: 'Stocks you\'re watching' },
        { id: 'dca-calculator', label: 'DCA Calculator', icon: Calculator, description: 'Dollar-cost averaging simulator' },
        { id: 'education', label: 'Education', icon: GraduationCap, description: 'Learn investing' },
        { id: 'settings', label: 'Settings', icon: Settings, description: 'Alerts & preferences' },
        { id: 'rebalancer', label: '⚖️ Rebalancer', icon: Scale },
        { id: 'paper-trading', label: '🎮 Paper Trading', icon: Target, description: 'Practice with $100K virtual' },
        { id: 'trading', label: '💰 Live Trading', icon: Zap, description: 'Real brokerage trading' }
      ]
    },
    {
      id: 'smart-money',
      label: 'Smart Money',
      icon: Building,
      color: '#06b6d4',
      usOnly: true, // Entire category is US only
      items: [
        { id: 'congress-trades', label: 'Congress Trades', icon: Building, description: 'Track politician stock trades', usOnly: true },
        { id: 'insider-trading', label: 'Insider Trading', icon: UserCheck, description: 'CEO & CFO stock buys/sells' },
        { id: 'whale-watcher', label: 'Whale Watcher', icon: Briefcase, description: 'What billionaires are holding', usOnly: true }
      ]
    },
{
  id: 'sports',
  label: 'Sports Market',
  icon: Trophy,
  color: '#00ffaa',
  items: [
    { id: 'sports-market',     label: '🏆 Public Sports Market',   icon: Trophy,   description: 'Trade sports franchises, betting & esports stocks' },
    { id: 'sports-private',    label: '🔒 Private Sports Market',  icon: Lock,     description: 'Tier A/B/C · shadow tokens · deal rooms · live raises' },
    { id: 'sports-org-portal', label: '🏟️ Org Application Portal', icon: Building, description: 'Leagues & franchises apply for Tier B certification' },
  ]
},

  ];

  // Filter categories and items based on selected market
  const categories = allCategories
    .filter(category => {
      // Hide entire category if it's US-only and we're in India
      if (selectedMarket === 'IN' && category.usOnly) {
        return false;
      }
      return true;
    })
    .map(category => ({
      ...category,
      items: category.items.filter(item => {
        // Hide US-only items when in India market
        if (selectedMarket === 'IN' && item.usOnly) {
          return false;
        }
        return true;
      })
    }))
    .filter(category => category.items.length > 0); // Remove empty categories

  const handleItemClick = (itemId) => {
    setActiveTab(itemId);
    setOpenDropdown(null);
  };

  const toggleDropdown = (categoryId, event) => {
    if (openDropdown === categoryId) {
      setOpenDropdown(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const dropdownWidth = 260;
      
      // Check if dropdown would overflow right edge
      const alignRight = (rect.left + dropdownWidth) > windowWidth;
      
      setDropdownPosition({
        top: rect.bottom + 8,
        left: alignRight ? 'auto' : rect.left,
        right: alignRight ? (windowWidth - rect.right) : 'auto',
        alignRight
      });
      setOpenDropdown(categoryId);
    }
  };

  // Check if current tab is in a category
  const isActiveInCategory = (category) => {
    return category.items.some(item => item.id === activeTab);
  };

  // Get current open category for rendering dropdown
  const getCurrentCategory = () => {
    return categories.find(cat => cat.id === openDropdown);
  };

  return (
    <>
    <style>{`
      nav.iq-nav::-webkit-scrollbar { height: 3px; }
      nav.iq-nav::-webkit-scrollbar-track { background: transparent; }
      nav.iq-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.4); border-radius: 2px; }
      nav.iq-nav::-webkit-scrollbar-thumb:hover { background: rgba(99,102,241,0.7); }
    `}</style>
        <div ref={navRef} style={{ position: 'relative', zIndex: 100 }}>
      <nav style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.5rem',
        background: 'rgba(15, 23, 42, 0.5)',
        borderRadius: '0.75rem',
        border: '1px solid #334155',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(99,102,241,0.4) transparent'
      }} className="iq-nav">

        {/* Home Button */}
        <button
          onClick={() => { setActiveTab('home'); setOpenDropdown(null); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.625rem 1rem',
            background: activeTab === 'home' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'transparent',
            border: 'none',
            borderRadius: '0.5rem',
            color: activeTab === 'home' ? 'white' : '#94a3b8',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'all 0.2s'
          }}
        >
          <Home size={18} />
        </button>

        {/* Learn Button */}
        <button
          onClick={() => { setActiveTab('learn'); setOpenDropdown(null); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.625rem 1rem',
            background: activeTab === 'learn' 
              ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' 
              : 'transparent',
            border: activeTab === 'learn' ? 'none' : '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '0.5rem',
            color: activeTab === 'learn' ? 'white' : '#a78bfa',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'all 0.2s'
          }}
        >
          <BookOpen size={18} />
          <span>Learn</span>
          <span style={{
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            fontSize: '0.6rem',
            fontWeight: '700',
            padding: '2px 6px',
            borderRadius: '4px'
          }}>
            FREE
          </span>
        </button>

        {/* Market Indicator Badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          padding: '0.375rem 0.625rem',
          background: selectedMarket === 'IN' ? 'rgba(249, 115, 22, 0.15)' : 'rgba(59, 130, 246, 0.15)',
          borderRadius: '0.375rem',
          fontSize: '0.7rem',
          fontWeight: '600',
          color: selectedMarket === 'IN' ? '#fb923c' : '#60a5fa',
          flexShrink: 0
        }}>
          {selectedMarket === 'IN' ? '🇮🇳 India' : '🇺🇸 US'}
        </div>

        {/* Category Dropdowns */}
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={(e) => toggleDropdown(category.id, e)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.625rem 0.875rem',
              background: isActiveInCategory(category) 
                ? `${category.color}20` 
                : openDropdown === category.id 
                  ? 'rgba(51, 65, 85, 0.5)' 
                  : 'transparent',
              border: isActiveInCategory(category) ? `1px solid ${category.color}40` : '1px solid transparent',
              borderRadius: '0.5rem',
              color: isActiveInCategory(category) ? category.color : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'all 0.2s'
            }}
          >
            <category.icon size={18} />
            <span>{category.label}</span>
            <ChevronDown 
              size={14} 
              style={{ 
                transition: 'transform 0.2s',
                transform: openDropdown === category.id ? 'rotate(180deg)' : 'rotate(0deg)'
              }} 
            />
          </button>
        ))}
        {/* Podcast Admin Button */}
        <button
          onClick={() => { setActiveTab('podcast-admin'); setOpenDropdown(null); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.625rem 0.875rem',
            background: activeTab === 'podcast-admin' 
              ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' 
              : 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '0.5rem',
            color: activeTab === 'podcast-admin' ? 'white' : '#a78bfa',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'all 0.2s'
          }}
        >
          🎙️ Podcast
        </button>
      </nav>

      {/* Dropdown Menu - Fixed position below clicked button */}
      {openDropdown && getCurrentCategory() && (
        <div 
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.alignRight ? 'auto' : dropdownPosition.left,
            right: dropdownPosition.alignRight ? dropdownPosition.right : 'auto',
            minWidth: '240px',
            maxWidth: 'calc(100vw - 2rem)',
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '0.5rem',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            animation: 'dropdownFade 0.2s ease-out'
          }}
        >
          {getCurrentCategory().items.map((item) => (
            <button
              key={item.id}
              onClick={() => handleItemClick(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                width: '100%',
                padding: '0.75rem',
                background: activeTab === item.id ? `${getCurrentCategory().color}15` : 'transparent',
                border: 'none',
                borderRadius: '0.5rem',
                color: activeTab === item.id ? getCurrentCategory().color : '#e2e8f0',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== item.id) {
                  e.currentTarget.style.background = 'rgba(51, 65, 85, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== item.id) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '0.5rem',
                background: `${getCurrentCategory().color}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <item.icon size={18} color={getCurrentCategory().color} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                  {item.label}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.125rem' }}>
                  {item.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* CSS for animation */}
      <style>{`
        @keyframes dropdownFade {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default Navigation;